import type { OrderItem } from '@/domain/Order';
import type {
  SupplementLabel,
  SupplementLabelIngredient,
} from '@/domain/SupplementLabel';

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

export function formatLabelMonthYear(dateInput?: string | null): string | null {
  if (!dateInput) return null;
  const date = new Date(dateInput);
  if (Number.isNaN(date.getTime())) return null;
  return `${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
}

export function formatNrvDisplay(
  nrvPercent: string | number | null | undefined,
): string {
  if (nrvPercent === null || nrvPercent === undefined || nrvPercent === '') {
    return '†';
  }
  const num =
    typeof nrvPercent === 'string' ? parseFloat(nrvPercent) : nrvPercent;
  if (Number.isNaN(num)) return '†';
  const rounded = Number.isInteger(num) ? String(num) : String(Math.round(num));
  return `${rounded}%`;
}

export function formatNetQuantityDisplay(
  packets: number,
  netQuantityGrams: number,
): string {
  const grams = Number.isInteger(netQuantityGrams)
    ? String(netQuantityGrams)
    : netQuantityGrams.toFixed(1).replace(/\.0$/, '');
  return `${packets} x packets (${grams} g)`;
}

/**
 * Upper label line from unique non-null `function_name` values on
 * label ingredients (GET orders/{id}/label). Falls back to top-level
 * `categories` if provided, otherwise the short generic line.
 */
export function buildCharacterisingSubstancesLine(
  ingredients: Array<SupplementLabelIngredient>,
  categories?: Array<string> | null,
): string {
  const normalizePrintCopy = (line: string) =>
    line.replace(/NAD⁺/g, 'NAD+').replace(/NAD\u207a/g, 'NAD+');

  const fromIngredients = ingredients
    .map((i) => i.function_name?.trim())
    .filter((name): name is string => !!name);

  const fromCategories = (categories ?? [])
    .map((c) => c.trim())
    .filter(Boolean);

  const groups = fromIngredients.length > 0 ? fromIngredients : fromCategories;

  if (groups.length === 0) {
    return 'Personalised food supplement for:';
  }

  return normalizePrintCopy(
    `Personalised food supplement with ${joinCategoryList(groups)} for:`,
  );
}

function joinCategoryList(categories: Array<string>): string {
  // Lowercase mid-sentence; only "Personalised" stays capitalised.
  const unique = [
    ...new Set(categories.map((c) => c.trim().toLowerCase()).filter(Boolean)),
  ];
  if (unique.length === 0) return '';
  if (unique.length === 1) return unique[0];
  if (unique.length === 2) return `${unique[0]} & ${unique[1]}`;
  return `${unique.slice(0, -1).join(', ')} & ${unique[unique.length - 1]}`;
}

export type TextSegment = { text: string; bold: boolean };

/** Deduped other-ingredients text from order item descriptions. */
export function formatOtherIngredients(items: Array<OrderItem>): string {
  const seen = new Set<string>();
  const parts: Array<string> = [];

  for (const item of items) {
    const description = item.description?.trim();
    if (!description) continue;

    for (const raw of description.split(/[,;]/)) {
      const text = raw.trim().replace(/\.+$/, '').toLowerCase();
      if (!text || seen.has(text)) continue;
      seen.add(text);
      parts.push(text);
    }
  }

  if (parts.length === 0) return '';
  parts[0] = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
  return parts.join(', ');
}

/** Unique allergens from label ingredients + top-level list. */
export function collectLabelAllergens(
  ingredients: Array<SupplementLabelIngredient>,
  topLevel?: Array<string> | null,
): Array<string> {
  const seen = new Set<string>();
  const out: Array<string> = [];

  for (const value of [
    ...ingredients.flatMap((ing) => ing.allergens ?? []),
    ...(topLevel ?? []),
  ]) {
    const trimmed = value.trim();
    const key = trimmed.toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(trimmed);
  }

  return out;
}

/** Bold allergen words in text (case-insensitive match, original casing kept). */
export function highlightAllergenWords(
  text: string,
  allergens: Array<string>,
): Array<TextSegment> {
  if (!text) return [];

  const terms = [
    ...new Set(allergens.map((a) => a.trim().toLowerCase()).filter(Boolean)),
  ].sort((a, b) => b.length - a.length);

  if (terms.length === 0) return [{ text, bold: false }];

  const pattern = new RegExp(
    `\\b(${terms.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})\\b`,
    'gi',
  );
  const termSet = new Set(terms);

  return text
    .split(pattern)
    .filter((part) => part.length > 0)
    .map((part) => ({
      text: part,
      bold: termSet.has(part.toLowerCase()),
    }));
}

export function earliestBestBefore(
  label: SupplementLabel | undefined,
  items: Array<OrderItem>,
): string | null {
  if (label?.best_before) {
    return formatLabelMonthYear(label.best_before);
  }

  const dates = items
    .map((i) => i.expire_date)
    .filter((d): d is string => !!d)
    .map((d) => new Date(d))
    .filter((d) => !Number.isNaN(d.getTime()))
    .sort((a, b) => a.getTime() - b.getTime());

  if (dates.length > 0) {
    return formatLabelMonthYear(dates[0].toISOString());
  }

  return null;
}

export const LEGAL_DISCLAIMER =
  "Do not exceed stated daily dose. Food supplements are not a substitute for a varied diet. Keep out of reach of young children. Not suitable for use by pregnant or lactating women. Prepared and issued according to the consumer's order. Follows the recommendations and advice of a doctor or pharmacist. Complete with food supplements and active ingredients available on the market.";
